/**
 * VsHeader — "You" (our layered avatar) vs the chosen rival, shown on Build + Review
 * (specs/challenge-flow-redesign — AC-2.x header, AC-4.2). For friend rivals it shows the aggregate
 * H2H string ("You lead 3–2") under VS, from the friends-list wins/losses (T-40 / design 2a).
 */

import React from 'react';
import { View, Text } from 'react-native';
import type { DisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { useUserProfile } from '../../../src/shared/contexts/UserProfileContext';
import { useCreatePage } from '../../../src/create/contexts/CreatePageContext';
import { getSafeDisplayName, isAvatarUrl } from '../../../src/create/helpers/createUi.helper';
import { getH2hSummary } from '../../../src/create/helpers/rivalRecord.helper';
import {
  CREATE_FLOW_TEST_IDS,
  OPEN_CHALLENGE_PARTICIPANT_ID,
} from '../../../src/create/constants/create.constants';
import { OPEN_CHALLENGE_OPPONENT_LABEL } from '../../../src/challenges/constants/challenges.constants';
import { DEFAULT_GROUP_IMAGE_SOURCE } from '../../../constants/assets';
import { vsHeaderStyles as styles } from './VsHeader.styles';

// Compact: this strip is fixed chrome on the Build screen; on short mobile-web viewports every
// pixel here is taken from the deck card's scroll area (which was clipping the market odds).
const AVATAR_SIZE = 32;

export const VsHeader: React.FC = () => {
  const { profile } = useUserProfile();
  const {
    friends,
    selectedFriendId,
    selectedGroupId,
    selectedGroup,
  } = useCreatePage();

  // "Post to anyone" rides the reserved participant id, so there is no rival friend to look up —
  // it gets the default group cover as its face instead of falling through to initials.
  const isOpenRival = selectedFriendId === OPEN_CHALLENGE_PARTICIPANT_ID;
  const rivalFriend =
    selectedFriendId && !isOpenRival ? friends.find((f) => f.id === selectedFriendId) : null;
  const rivalName = selectedGroupId
    ? selectedGroup?.name ?? 'Group'
    : isOpenRival
      ? OPEN_CHALLENGE_OPPONENT_LABEL
      : getSafeDisplayName(rivalFriend?.name ?? 'Rival');
  // Aggregate H2H only for a friend rival (groups and the open board have no single opponent record).
  const h2h = !selectedGroupId && rivalFriend ? getH2hSummary(rivalFriend) : null;

  return (
    <View style={styles.card} testID={CREATE_FLOW_TEST_IDS.vsHeader}>
      <View style={styles.side}>
        <View style={[styles.tile, styles.youTile]}>
          <FriendAvatar
            avatarId={(profile?.avatarId ?? undefined) as DisplayAvatarId | undefined}
            avatarConfig={profile?.avatarConfig ?? null}
            size={AVATAR_SIZE}
            nameForInitials="You"
            avatarRounded={false}
          />
        </View>
        <Text style={styles.name} numberOfLines={1}>You</Text>
      </View>

      <View style={styles.center}>
        <Text style={styles.vs}>VS</Text>
        {h2h && (
          <Text style={styles.h2h} numberOfLines={1}>
            {h2h}
          </Text>
        )}
      </View>

      <View style={styles.side}>
        <View style={[styles.tile, styles.rivalTile]}>
          <FriendAvatar
            // Group rival: the group's cover image (or the default group cover), same as GroupCard.
            // Open rival ("anyone"): the default group cover stands in for the unknown taker.
            imageSource={
              selectedGroupId
                ? selectedGroup?.imageUrl
                  ? { uri: selectedGroup.imageUrl }
                  : DEFAULT_GROUP_IMAGE_SOURCE
                : isOpenRival
                  ? DEFAULT_GROUP_IMAGE_SOURCE
                  : undefined
            }
            avatarId={
              (selectedGroupId
                ? undefined
                : (rivalFriend?.avatarId ?? undefined)) as DisplayAvatarId | undefined
            }
            avatarConfig={selectedGroupId ? null : rivalFriend?.avatarConfig ?? null}
            fallbackUrl={
              !selectedGroupId && isAvatarUrl(rivalFriend?.avatar ?? '')
                ? rivalFriend?.avatar
                : undefined
            }
            size={AVATAR_SIZE}
            nameForInitials={rivalName}
            avatarRounded={false}
          />
        </View>
        <Text style={styles.name} numberOfLines={1}>{rivalName}</Text>
      </View>
    </View>
  );
};
